import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CEFR_LEVELS, fmt, levelOf, nextLevelOf } from 'pages/VocabularyTest/cefr';

interface CEFRScaleBarProps {
  count: number;
  empty?: boolean;
}

const CEFRScaleBar: React.FC<CEFRScaleBarProps> = ({ count, empty }) => {
  const cur = empty ? null : levelOf(count);
  const curIdx = cur ? CEFR_LEVELS.findIndex((l) => l.id === cur.id) : -1;
  const next = cur ? nextLevelOf(cur) : null;
  const within =
    cur && next
      ? (count - cur.start) / (next.start - cur.start)
      : cur
      ? 1
      : 0;
  const markerPct = empty
    ? 0
    : ((curIdx + Math.min(Math.max(within, 0.04), 0.96)) / CEFR_LEVELS.length) *
      100;
  const need = next ? next.start - count : 0;

  return (
    <Box>
      <Stack
        direction={'row'}
        alignItems={'baseline'}
        justifyContent={'space-between'}
        sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          {'Шкала CEFR'}
        </Typography>
        {!empty && next && (
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {'ще '}
            <Box component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
              {fmt(need)}
            </Box>
            {' слів до '}
            <Box component={'b'} sx={{ color: 'primary.main', fontWeight: 800 }}>
              {next.id}
            </Box>
          </Typography>
        )}
        {!empty && !next && (
          <Typography
            sx={{ color: '#3B6D11', fontSize: 14, fontWeight: 700 }}
          >
            {'Максимальний рівень досягнуто'}
          </Typography>
        )}
      </Stack>

      <Box sx={{ position: 'relative', pt: empty ? 0 : '26px' }}>
        {!empty && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: `${markerPct}%`,
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 3,
            }}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 999,
                color: 'primary.main',
                fontSize: 13,
                fontWeight: 800,
                px: 1.1,
                py: '2px',
                whiteSpace: 'nowrap',
              }}
            >
              {`${fmt(count)} слів`}
            </Box>
            <Box
              sx={{ bgcolor: 'primary.main', height: 8, width: '2px' }}
            />
          </Box>
        )}

        <Stack direction={'row'} spacing={'4px'}>
          {CEFR_LEVELS.map((l, i) => {
            const done = !empty && i < curIdx;
            const current = !empty && i === curIdx;
            return (
              <Box key={l.id} sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.purple',
                    borderRadius: 999,
                    height: 12,
                    opacity: empty ? 0.4 : 1,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {done && (
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        inset: 0,
                        opacity: 0.55,
                        position: 'absolute',
                      }}
                    />
                  )}
                  {current && (
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        bottom: 0,
                        left: 0,
                        position: 'absolute',
                        top: 0,
                        width: `${Math.max(within * 100, 6)}%`,
                      }}
                    />
                  )}
                </Box>
                <Stack alignItems={'center'} sx={{ mt: '7px' }}>
                  <Typography
                    sx={{
                      color: current
                        ? 'primary.main'
                        : done
                        ? 'text.primary'
                        : 'text.secondary',
                      fontSize: { xs: 12, md: 14 },
                      fontWeight: current ? 800 : 700,
                    }}
                  >
                    {l.id}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      display: { xs: 'none', sm: 'block' },
                      fontSize: 11,
                      mt: '1px',
                    }}
                  >
                    {fmt(l.start)}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default CEFRScaleBar;
